import React, { useState } from 'react';
import PanelHeader from 'components/Panel/components/PanelHeader';
import PanelBody from 'components/Panel/components/PanelBody';
import CurrenciesSelect from 'components/CurrencySelect';
import { formatDate, formatNumber, getIconFile } from 'utils/functions';
import { Building } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Breadcrumb, StatCard, Tabs } from 'xland-ui';
import { useSelector } from 'react-redux';
import SectionHeader from 'components/SectionHeader';
import Panel from 'components/Panel';
import { useCounter } from 'utils/customHooks';
import Chat from 'components/Chat';
import PopupEditInvestment from './PopupEditInvestment';
import InvestmentTable from './InvestmentTable';
import InvestmentChart from './InvestmentChart';
import { useInvestorAssets } from '../hooks/useInvestorAssets';
import EmptyInvestments from './EmptyInvestments';
import './styles.scss';

function InvestorInvestments() {
  const literals = useSelector(state => state.i18n.literals.investmentAssets);
  const literalsCommon = useSelector(state => state.i18n.literals.common);
  const tableRefreshFlag = useCounter();
  const [groupBy, setGroupBy] = useState('investments');
  const [editInvestment, setEditInvestment] = useState(false);
  const [searchParams] = useSearchParams();

  const { currency } = useSelector(state => state.global.investments);
  const currencySymbol = literalsCommon.currenciesSymbols[currency];

  const { loading, operations, stats } = useInvestorAssets(
    searchParams.get('organizations'),
    groupBy,
    tableRefreshFlag.value,
  );

  const mockData = [
    {
      name: 'Q4 2023 Financial Report.pdf',
      date: '2024-01-15',
    },
    {
      name: 'Investment Agreement.png',
      date: '2023-12-01',
    },
    {
      name: 'Due Diligence Report.xls',
      date: '2023-11-15',
    },
  ];

  if (!loading && !operations?.total) {
    return (
      <>
        <Breadcrumb routes={[{ name: literals.portfolio }]} />
        <EmptyInvestments />
      </>
    );
  }

  return (
    <>
      <Breadcrumb routes={[{ name: literals.portfolio }]} />
      <SectionHeader
        tabs={[{ key: 'dashboard', name: 'Dashboard' }]}
        defaultTab='dashboard'
        actions={(
          <span className='last-investment-date'>
            {literals.valuationData}
            {' '}
            {stats?.lastDate ? formatDate(stats.lastDate, { format: 'short' }) : '-'}
            <CurrenciesSelect mode='investments' />
          </span>
        )}
      >
        {tab => tab === 'dashboard' && (
          <>
            <div className='row mb-4'>
              <div className='col-12 col-lg-3 col-sm-6'>
                <StatCard
                  loading={loading}
                  title={literals.portfolioValuation}
                  value={formatNumber(stats?.totalValuation, '-', { decimals: '', symbol: currencySymbol })}
                  icon={<Building />}
                />
              </div>
              <div className='col-12 col-lg-3 col-sm-6'>
                <StatCard
                  loading={loading}
                  title={literals.realizedValue}
                  value={formatNumber(stats?.realizedValue, '-', { decimals: '', symbol: currencySymbol })}
                  icon={<Building />}
                />
              </div>
              <div className='col-12 col-lg-3 col-sm-6'>
                <StatCard
                  loading={loading}
                  title={literals.totalInvested}
                  value={formatNumber(stats?.totalInvested, '-', { decimals: '', symbol: currencySymbol })}
                  icon={<Building />}
                />
              </div>
              <div className='col-12 col-lg-3 col-sm-6'>
                <StatCard
                  loading={loading}
                  title={literals.multiple}
                  value={formatNumber(stats?.multiple, '-', { decimals: '', symbol: 'x' })}
                  icon={<Building />}
                />
              </div>
            </div>
            <div className='row mb-4'>
              <div className='col-12 col-md-6'>
                <Panel>
                  <PanelHeader title={literals.portfolioExposure} subtitle={literals.portfolioExposureSubtitle} />
                  <PanelBody>
                    <InvestmentChart
                      literals={{ ...literals, ...literalsCommon }}
                      operations={operations}
                    />
                  </PanelBody>
                </Panel>
              </div>
              <div className='col-12 col-md-6'>
                <Panel className='documents-panel'>
                  <PanelHeader title={literals.documents} subtitle={literals.documentsSubtitle} />
                  <PanelBody>
                    <div className='row'>
                      {[...mockData, ...mockData, ...mockData, ...mockData].filter((item, i) => i < 8).map((doc, index) => (
                        <div key={index} className='col-lg-6 col-12'>
                          <div className='document-item'>
                            <span className='document-icon'>{getIconFile(doc.name, 30)}</span>
                            <div className='document-info'>
                              <div className='document-name'>{doc.name}</div>
                              <div className='document-date'>{doc.date}</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </PanelBody>
                </Panel>
              </div>
            </div>

            <div className='d-flex justify-content-between mt-4 mb-4 '>
              <h4 className='fc-primary'>{literals.investments}</h4>
              <Tabs
                tabs={[
                  { key: 'investments', name: literals.byInvestments },
                  { key: 'type', name: literals.byType },
                  { key: 'status', name: literals.byStatus },
                ]}
                activeTab={groupBy}
                onClick={setGroupBy}
              />
            </div>
            <Chat context={`El usuario tiene las siguientes inversiones: ${JSON.stringify(operations)}`} />
            <InvestmentTable
              literals={{ ...literals, ...literalsCommon }}
              operations={operations}
              refresh={tableRefreshFlag.increase}
              setEditInvestment={setEditInvestment}
              groupBy={groupBy}
              loading={loading}
              currencySymbol={currencySymbol}
            />
            <div className='disclaimer mt-5'>
              <h4>{literals.disclaimer}</h4>
              <div dangerouslySetInnerHTML={{ __html: literals.disclaimerText }} />
            </div>
          </>
        )}
      </SectionHeader>
      {editInvestment && (
        <PopupEditInvestment
          asset={editInvestment.id ? editInvestment : null}
          organizations={editInvestment.id
            ? editInvestment.organization
            : searchParams.get('organizations') || ''}
          onClose={() => setEditInvestment(null)}
          onSubmit={tableRefreshFlag.increase}
        />
      )}
    </>
  );
}

export default InvestorInvestments;
